/**
 * Hold the chat queue stream open for as long as the admin is logged in.
 *
 * Mounted once, in the layout, rather than on the Live Chats page — the point is
 * to be told about someone waiting while you are looking at something else.
 *
 * `EventSource` cannot set an `Authorization` header, so the access token rides
 * in the query string and the server accepts it there for this one route.
 *
 * **This deliberately does not stop when the tab is hidden**, which it used to.
 * The gating was lifted from `OperationsDashboard`, where it is right: nobody
 * needs a dashboard to refresh behind their back. Here it removed the alert in
 * exactly the case the alert is for. An admin reading email in another tab is
 * not an admin who has stopped being on duty, and a chime that only plays when
 * you are already looking at the page is not a notification. The cost is one
 * held SSE connection per logged-in admin — the same one the page already paid
 * while focused.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@respectly/utils/auth';
import { API_ENDPOINTS } from '@respectly/api-client';
import type { ChatQueueCountsDto, ChatVisitorAlertDto } from '@respectly/shared-types';
import { useToast } from '@respectly/ui';
import { useChatQueueStore } from '../stores/useChatQueueStore';
import { playVisitorChime, playHandoffChime } from '../lib/notificationSound';
import { showDesktopNotification } from '../lib/desktopNotification';

const RECONNECT_DELAY_MS = 5_000;

/**
 * How quiet a conversation has to go before the next thing on it chimes again.
 *
 * Without this, a conversation is announced once and then never again for as long
 * as the portal stays open — so a visitor who came by this morning and returns
 * after lunch arrives in silence. Thirty minutes is long enough that nobody is
 * chimed at twice for one continuous conversation, and short enough that coming
 * back genuinely reads as coming back.
 */
const RE_ANNOUNCE_AFTER_MS = 30 * 60 * 1000;

function streamUrl(token: string): string {
  const base = (import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/$/, '');
  const path = API_ENDPOINTS.SUPER_ADMIN.CHAT.STREAM;
  return `${base}${path}${path.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

/** "Someone", or their name if we have it. */
function who(alert: ChatVisitorAlertDto): string {
  return alert.visitorName?.trim() || 'Someone';
}

export function useChatQueueStream(): void {
  const { getAccessToken } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const sourceRef = useRef<EventSource | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The previous count, kept in a ref so the toast fires on a *rise* rather than
  // on every event. Someone already handling three waiting chats does not need to
  // be told about them again every five seconds.
  const previousRef = useRef<number | null>(null);
  // When each conversation was last announced. A visitor who opens the panel and
  // then types is one arrival, not two, and the server reports both — but the
  // same person coming back an hour later is a new arrival, so this is a
  // timestamp rather than a "seen" flag.
  const announcedRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    let mounted = true;
    const store = useChatQueueStore.getState();

    const connect = (): void => {
      if (!mounted || sourceRef.current) return;
      void getAccessToken().then((token) => {
        if (!mounted || !token || sourceRef.current) return;

        const source = new EventSource(streamUrl(token));
        sourceRef.current = source;

        source.addEventListener('open', () => useChatQueueStore.getState().setConnected(true));

        source.addEventListener('queue', (event) => {
          try {
            const counts = JSON.parse((event as MessageEvent<string>).data) as ChatQueueCountsDto;
            useChatQueueStore.getState().set(counts);
            const before = previousRef.current;
            previousRef.current = counts.waiting;
            if (before !== null && counts.waiting > before) {
              toast({
                title:
                  counts.waiting === 1
                    ? 'Someone is waiting to chat'
                    : `${counts.waiting} people are waiting to chat`,
                description: 'Open Live Chats to pick it up.',
                variant: 'warning',
              });
              // The one alert here with a clock on it: somebody asked for a
              // person, not just a widget being open. Louder than the visitor
              // chime below, and on its own — this used to fire silently.
              if (useChatQueueStore.getState().soundEnabled) playHandoffChime();
            }
          } catch {
            // A malformed frame is not worth breaking the stream over.
          }
        });

        source.addEventListener('visitor', (event) => {
          try {
            const alert = JSON.parse(
              (event as MessageEvent<string>).data
            ) as ChatVisitorAlertDto;

            useChatQueueStore.getState().pushVisitor(alert);

            // The list above is updated for every report. Interrupting somebody
            // is rationed: once per conversation, until it has been quiet long
            // enough that the next thing to happen on it counts as a fresh visit.
            const now = Date.now();
            const announced = announcedRef.current.get(alert.sessionId);
            if (announced !== undefined && now - announced < RE_ANNOUNCE_AFTER_MS) return;
            announcedRef.current.set(alert.sessionId, now);

            const title =
              alert.kind === 'message'
                ? `${who(alert)} is asking about something`
                : alert.kind === 'returning'
                  ? `${who(alert)} is back on the chat`
                  : `${who(alert)} opened the chat on ${alert.brandName}`;
            const description = alert.preview
              ? `“${alert.preview}” · ${alert.brandName}`
              : 'Open Live Chats to say hello.';

            toast({ title, description, variant: 'info' });

            if (useChatQueueStore.getState().soundEnabled) playVisitorChime();

            showDesktopNotification({
              title,
              body: description,
              tag: `chat-visitor-${alert.sessionId}`,
              onClick: () =>
                void navigate({
                  to: '/live-chats',
                  search: { session: alert.sessionId },
                }),
            });
          } catch {
            // Same as above: drop the frame, keep the stream.
          }
        });

        source.onerror = () => {
          source.close();
          sourceRef.current = null;
          useChatQueueStore.getState().setConnected(false);
          if (mounted) reconnectRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
        };
      });
    };

    const stop = (): void => {
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      useChatQueueStore.getState().setConnected(false);
    };

    connect();

    // Not a teardown any more — a repair. Browsers freeze timers in background
    // tabs, so a connection that dropped while hidden may still be sitting on an
    // unfired reconnect; coming back is a good moment to check.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible' && !sourceRef.current) connect();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
      store.setConnected(false);
    };
  }, [getAccessToken, toast, navigate]);
}
